
import * as React from "react";
import PropTypes from "prop-types";
import { createCustomEqual } from "fast-equals";
import { isLatLngLiteral } from "@googlemaps/typescript-guards";
import { APIProvider, Map as GoogleMap, useApiIsLoaded, useMap, AdvancedMarker } from '@vis.gl/react-google-maps';
import MarkerContainer from "../containers/MarkerContainer";
import isTimingOkay from '../utilities/isTimingOkay';

import locationMarker from '../images/maps-your-location.png';

const Map = (props) => {
  const { items, searchPosition, zoom, center, isDirty, mapBoundsSet, onRecenter } = props;

  const [searchMarker, setSearchMarker] = React.useState(null);
  const [resultMarker, setResultMarker] = React.useState(null);

  // apply search position marker
  React.useEffect(() => {
    if (searchPosition.lat) {
      setSearchMarker(
        <AdvancedMarker position={searchPosition} zIndex={100}>
          <img src={locationMarker} width={48} height={48} alt="" />
        </AdvancedMarker>
      );
    } else {
      setSearchMarker(null);
    }
  }, [searchPosition]);

  // apply result markers
  React.useEffect(() => {
    const currentResultMarker = items && items.length > 0 ? items.map(i => {
      if (isTimingOkay(i)) {
        return <MarkerContainer key={i.parcelShopNumber} {...i} />
      }
    }
    ) : null;
    setResultMarker(currentResultMarker);
  }, [items]);

  return (
    <div style={{ display: "flex", height: "100%" }}>
      <APIProvider apiKey={"AIzaSyB12xvHjP7pBsbL0IaitKFQOSSrnbWk7zU"} libraries={["places", "marker", "geocoding", "core"]}>
        <GoogleMapComponent
          center={center}
          zoom={zoom}
          style={{ flexGrow: "1", height: "100%" }}
          items={items}
          onRecenter={onRecenter}
          mapBoundsSet={mapBoundsSet}
          isDirty={isDirty}
        >
          {resultMarker}
          {searchMarker}
        </GoogleMapComponent>
      </APIProvider>
    </div>
  );
};

const GoogleMapComponent = ({
  onClick,
  children,
  style,
  items,
  ...options
}) => {

  const map = useMap();
  const apiIsLoaded = useApiIsLoaded();

  const [isGoogleApiLoaded, setGoogleApiLoaded] = React.useState(false);

  const onDragEnd = () => {
    const center = map.getCenter();
    const position = { lat: center.lat(), lng: center.lng() };
    options.onRecenter(position);
  };

  const setBounds = () => {
    if (map && items.length > 0) {
      google.maps.event.trigger(map, "resize"); // resize for mobile view
      const bounds = new window.google.maps.LatLngBounds();
      items.forEach(({ lat, lng }) => {
        bounds.extend({ lat, lng });
      });
      map.fitBounds(bounds);
    }
  }

  // not using a timeout for checking the api loading status will cause an error
  React.useEffect(() => {
    setTimeout(() => {
      if (apiIsLoaded) {
        setGoogleApiLoaded(true);
      }
    }, 100);
  }, [apiIsLoaded]);

  React.useEffect(() => {
    if (options.isDirty) {
      setBounds();
      options.mapBoundsSet();
    }
  }, [options.isDirty]);

  React.useEffect(() => {
    setBounds();
  }, [items, map]);

  return (
    isGoogleApiLoaded ?
      <GoogleMap mapId={'Hermes'} fullscreenControl={true} zoomControl={true} disableDefaultUI gestureHandling={'greedy'} defaultZoom={options.zoom} defaultCenter={options.center} onClick={options.onClick} onDragend={onDragEnd}>
        {children}
      </GoogleMap>
      : null
  );
};

// below code was recommended by Google Maps API, not sure if it is necessary
const deepCompareEqualsForMaps = createCustomEqual(
  (deepEqual) => (a, b) => {
    if (
      isLatLngLiteral(a) ||
      a instanceof google.maps.LatLng ||
      isLatLngLiteral(b) ||
      b instanceof google.maps.LatLng
    ) {
      return new google.maps.LatLng(a).equals(new google.maps.LatLng(b));
    }

    return deepEqual(a, b);
  }
);

function useDeepCompareMemoize(value) {
  const ref = React.useRef();

  if (!deepCompareEqualsForMaps(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

function useDeepCompareEffectForMaps(
  callback,
  dependencies
) {
  React.useEffect(callback, dependencies.map(useDeepCompareMemoize));
}

Map.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      parcelShopNumber: PropTypes.string.isRequired,
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
      countryIsoA2Code: PropTypes.string.isRequired,
    })
  ),
  isDirty: PropTypes.bool.isRequired,
  mapBoundsSet: PropTypes.func.isRequired,
  onRecenter: PropTypes.func.isRequired,
};

export default Map;
